import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import PollIcon from '@mui/icons-material/Poll';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DescriptionIcon from '@mui/icons-material/Description';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import BuildIcon from '@mui/icons-material/Build';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SchoolIcon from '@mui/icons-material/School';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase';

/**
 * DataInputsStatus — live "Org Data Inputs" status view shared by the Org IT
 * Portal (IT-facing) and the IA IT Portal Hub (operator-facing).
 *
 * Shows real record counts for the data sources already flowing into REPSit.ai
 * for the active organization, plus the client systems that connect during
 * onboarding/integration. No fabricated numbers — every count is a live query.
 */

const FUTURE_SYSTEMS = [
  {
    id: 'cap',
    label: 'Corrective Action Program (CAP)',
    icon: BuildIcon,
    description: 'Condition reports, corrective actions, and closure data from your CAP system.',
  },
  {
    id: 'safety',
    label: 'Safety / Incident System',
    icon: ReportProblemIcon,
    description: 'Incidents, near-misses, and observations from your safety management system.',
  },
  {
    id: 'lms',
    label: 'LMS / Training',
    icon: SchoolIcon,
    description: 'Training completion and qualification data from your learning management system.',
  },
  {
    id: 'coaching',
    label: 'Coaching & Observation',
    icon: RecordVoiceOverIcon,
    description: 'Field coaching sessions and observation records.',
  },
];

async function countQuery(col, constraints) {
  const snap = await getDocs(query(collection(db, col), ...constraints));
  return snap.size;
}

export default function DataInputsStatus({ organizationId }) {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    if (!organizationId) {
      setCounts(null);
      return undefined;
    }
    let cancelled = false;
    setCounts(null);

    (async () => {
      const orgWhere = where('organizationId', '==', organizationId);
      const result = { surveys: null, trls: null, documents: null, healthMessages: null };

      try {
        const cutoff = Timestamp.fromMillis(Date.now() - 30 * 24 * 3600 * 1000);
        result.surveys = await countQuery('Hugo_Responses', [orgWhere, where('completedAt', '>=', cutoff)]);
      } catch (e) {
        console.error('DataInputsStatus: surveys count failed', e);
      }
      try {
        result.trls = await countQuery('Hugo_TRLs', [orgWhere]);
      } catch (e) {
        console.error('DataInputsStatus: TRL count failed', e);
      }
      try {
        const docCount = await countQuery('Hugo_Documents', [orgWhere]);
        if (docCount > 0) {
          result.documents = docCount;
        } else {
          result.documents = await countQuery('Hugo_CaseStudies', [orgWhere]);
        }
      } catch (e) {
        try {
          result.documents = await countQuery('Hugo_CaseStudies', [orgWhere]);
        } catch (e2) {
          console.error('DataInputsStatus: documents count failed', e2);
        }
      }
      try {
        result.healthMessages = await countQuery('Hugo_HealthMessages', [orgWhere]);
      } catch (e) {
        console.error('DataInputsStatus: health messages count failed', e);
      }

      if (!cancelled) setCounts(result);
    })();

    return () => { cancelled = true; };
  }, [organizationId]);

  if (!organizationId) {
    return (
      <Alert severity="info">
        No organization selected — data input status appears once your organization is set up.
      </Alert>
    );
  }

  if (!counts) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const liveInputs = [
    {
      id: 'surveys',
      label: 'Surveys',
      icon: PollIcon,
      status: 'Connected — live',
      count: counts.surveys,
      countLabel: 'responses (30d)',
      description: 'REPS survey responses feeding OE scores, trends, and warnings.',
    },
    {
      id: 'trls',
      label: 'OE Conditions',
      icon: WarningAmberIcon,
      status: 'Connected — live',
      count: counts.trls,
      countLabel: 'OE Conditions',
      description: 'Drift, Traps, Roadblocks & Landmines logged by team members.',
    },
    {
      id: 'documents',
      label: 'Learning Library & Documents',
      icon: DescriptionIcon,
      status: 'Connected',
      count: counts.documents,
      countLabel: 'documents',
      description: 'Uploaded documents and incident case studies analyzed by the assistant.',
    },
    {
      id: 'health',
      label: 'OE Health Alerts',
      icon: HealthAndSafetyIcon,
      status: 'Connected — Divergence Detection',
      count: counts.healthMessages,
      countLabel: 'messages',
      description: 'Alerts written when survey scores and field data disagree.',
    },
  ];

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {liveInputs.map((input) => {
          const Icon = input.icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={input.id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Icon sx={{ color: 'primary.main' }} />
                    <Typography variant="subtitle2" fontWeight={700}>{input.label}</Typography>
                  </Box>
                  <Chip label={input.status} size="small" color="success" variant="outlined" sx={{ mb: 1 }} />
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="h5" fontWeight={700}>
                    {input.count != null ? input.count.toLocaleString() : '—'}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                      {input.countLabel}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {input.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Typography variant="h6" gutterBottom>Client Systems</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        These organizational source systems connect during client integration to enrich divergence
        detection with actual field data.
      </Typography>
      <Grid container spacing={3}>
        {FUTURE_SYSTEMS.map((sys) => {
          const Icon = sys.icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={sys.id}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Icon sx={{ color: 'text.secondary' }} />
                    <Typography variant="subtitle2" fontWeight={700}>{sys.label}</Typography>
                  </Box>
                  <Chip label="Not connected" size="small" sx={{ mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {sys.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                    Connects during client integration.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
